/**
 * Paris's Eiffel Tower, in the shared 120×120 LINE asset box.
 *
 * TRACED from the theme's own icon, not invented: the tower that fills the
 * middle of cities/paris-theme/icon.png, drawn there as a lattice silhouette in
 * #603352 against the sunset. The icon was masked on that exact colour and
 * scanned row by row, so the PROPORTIONS below are measured off the art rather
 * than eyeballed — apex at y 33, second platform y 232-240, first platform
 * y 294-316, feet at y 365, half-widths 8 / 17 / 23 / 45 / 66 at those stations.
 * Normalised, the icon's tower is 0.40 as wide at the foot as it is tall, its
 * second platform sits 59% of the way down and its first platform 79-85% down,
 * which is the real tower to within a couple of percent.
 *
 * It carries the Home tab AND the passcode dots, so the geometry lives here and
 * both import it — the rule the Everest summit, the Sequoia tree, the Autumn
 * maple and the Istanbul tower already follow. Copies drift; a module cannot.
 *
 * ---------------------------------------------------------------------------
 * HOW THIS IS KEPT DISTINCT FROM TOKYO'S TOWER
 *
 * Tokyo Tower is a copy of the Eiffel, so line-tower-glyph and this file draw
 * structurally the same object and would happily read as one silhouette in two
 * tints. They were measured against each other from rendered masks, not judged
 * by eye. The separation that came out:
 *
 *                                   THIS GLYPH        line-tower-glyph
 *   foot span ÷ ink height          0.695             0.881
 *   flare (foot ÷ span at mid-ht)   1.74              4.40
 *   span at 40% of the height       18.4u             13.8u  (Tokyo's waist)
 *   sharp ledges IN the silhouette  3                 0
 *   widest platform                 60.0u, 65% down   none in the outline
 *   bottom opening                  41.0 × 22.4u      34.0 × 15.9u
 *   opening width ÷ foot span       0.605             0.450
 *
 * In words: Tokyo is a squat wide triangle that pinches to a waist at 40% and
 * then flares hard, and its two decks are coloured RECTANGLES painted on top of
 * a body that has no steps in it at all — so Tokyo's OFF silhouette is a plain
 * flared A with a V-notch. This tower is a third narrower for its height, widens
 * monotonically with no waist, and its three platforms are cut INTO the outline
 * as real steps, so they survive into the OFF state where Tokyo's do not. That,
 * plus a round arch against Tokyo's pointed notch, is the whole separation.
 *
 * The commonly repeated claim that the Eiffel's outline is concave where Tokyo's
 * is straight does NOT survive measurement and is not what this file leans on:
 * both towers' leg curves come out within 0.1u of a straight chord. The steps
 * and the arch do the work.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS LOAD-BEARING — do not "clean this up":
 *
 *  - THE ARCH IS A REAL HOLE, not a painted-on shape. It is cut as part of the
 *    one closed contour of EIFFEL_SOLID (down the right leg's outer edge, left
 *    along the ground, up and over the arch, back down, left again to the far
 *    foot), so it is a hole under any fill-rule and it is still there in the
 *    flat OFF state. At 22px it is the single cue that says Eiffel and not
 *    "generic tower", and it is the first thing to check after any edit.
 *  - THE ARCH IS ELLIPTICAL AND LOW-SLUNG — 41 wide by 22.4 tall. An earlier
 *    pass parameterised its cubic with the control point ABOVE the crown, which
 *    produced a tall gothic point that bit into the platform and read as a
 *    church door. Wide and round is Eiffel; tall and pointed is not.
 *  - THREE PLATFORMS, NOT TWO. The real tower has a first platform, a second,
 *    and the little top pavilion under the spire. The top one is nearly invisible
 *    below 40px and is kept anyway: at 43-48px it is what makes the count read
 *    as three tiers, and three tiers is the fastest way the eye separates this
 *    from Tokyo's one-fat-deck-plus-antenna.
 *  - THE LEDGES ARE EXAGGERATED. The icon's first platform sticks out 11 units
 *    per side out of a 66-unit foot; scaled honestly into this box that is 4.4
 *    units, which is 0.8px at passcode size and simply is not there. They are
 *    drawn stepping out 3.9 / 9.9 / 14.3 units instead. Trueing them back to
 *    the icon is the change that fails the 22px gate.
 *  - THE LEG ZONE IS STRETCHED. On the real tower the legs occupy the bottom
 *    18% of the height; here they take 23%, because the arch has to be tall
 *    enough to survive as a hole at 22px, where 18% is two pixels.
 *  - THE LAMP LIPS ARE INSET from the ends of each ledge. Champagne is only
 *    1.54:1 against the near-white iOS-26 glass, so a lip running the full width
 *    of a platform dissolves the ledge's outer corners into the background and
 *    the platform reads narrower than it is. Held back 3.2 units per side, the
 *    corners stay in body gold and the ledge keeps its full span.
 *  - NOTHING ELSE SURVIVES. The lattice mesh, the criss-cross truss and the
 *    deck lamps were all tried and all dropped: at 22px the mesh is sub-pixel
 *    and only muddies the silhouette, the same lesson line-tower-glyph already
 *    records for its own truss. Silhouette + arch + three ledge bands, nothing
 *    more.
 *
 * The fit, measured off the paths themselves: ink spans y 12 → 110 (81% of the
 * long axis) and x 26 → 94 (57% of the short one), centred on the box's own
 * vertical axis. The box is deliberately not filled sideways — the Eiffel is a
 * tall thing and the air either side of the feet is what says so.
 *
 * Every subpath is wound the same way — verified by signed area, not assumed:
 * SOLID +2655, BASE +1492 and all nine bands positive, so a nonzero fill unions
 * them and no band can punch itself into a hole where it overlaps.
 *
 * ON  = the floodlit tower, four tones.
 * OFF = EIFFEL_SOLID alone in one flat muted tone, arch still cut through it.
 *
 * DRAW ORDER for the ON state — the parts overlap, so it matters:
 *   EIFFEL_SOLID          in EIFFEL_GOLD
 *   EIFFEL_BASE           in EIFFEL_DEEP   (the floodlit feet)
 *   EIFFEL_DECKS          in EIFFEL_DEEP   (the three platform bands)
 *   EIFFEL_LAMPS          in EIFFEL_LAMP   (the lamp rows along each platform)
 *   EIFFEL_LEDGE_SHADOW   in EIFFEL_SHADE  (the shadow line under each ledge)
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** The box's vertical axis. The tower is symmetric about it. */
const CX = 60;

/* ---------------------------------------------------------------- stations --
 * Every y below is a station read off the icon and rescaled; every half-width
 * is the icon's, widened where the note above says so.
 */

/** Tip of the mast, and the ground the feet stand on. */
const TIP = 12, GROUND = 110;
/** The mast, and the top pavilion (the third platform) it stands on. */
const MAST_HALF = 1.7, BLOCK_TOP = 21, BLOCK_HALF = 5.4, BLOCK_BOT = 26;
/** The shaft steps IN below the pavilion — that step is what makes the pavilion
 *  read as a platform instead of a bulge in the spire. */
const SHAFT_TOP = 4.3;
/** The second platform, and the shaft either side of it. */
const P2_TOP = 55, P2_BOT = 63, P2_HALF = 19.5, P2_ABOVE = 9.8, P2_BELOW = 11.8;
/** The first platform — the widest thing in the outline — and the column above. */
const P1_TOP = 76, P1_BOT = 85, P1_HALF = 30, P1_ABOVE = 16;
/** The legs, from where they leave the first platform out to the feet. */
const LEG_TOP = 20.5, FOOT_HALF = 34;
/** The arch: half its opening at ground level, and the height of its crown. */
const ARCH_HALF = 20.5, ARCH_CROWN = 87.5;
/** Circle-to-cubic constant, for the elliptical arch. */
const KAPPA = 0.5523;
/** Band thicknesses: the lamp row on top of a ledge, its shadow underneath, and
 *  how far the lamp row is held back from the ledge's ends. */
const LAMP_H = 3.2, SHADE_H = 2.2, LAMP_INSET = 3.2;

/* ----------------------------------------------------------------- helpers -- */

type Pt = [ number, number ];

/** A quadratic chain through the midpoints of a sampled polyline, so a taper
 *  comes back out tangent-continuous with no facets. Same trick the Autumn
 *  maple uses on its traced outline. */
function chain( pts: Pt[] ): string {
  let d = '';
  for ( let i = 1; i < pts.length - 1; i++ ) {
    const [ x, y ] = pts[ i ], [ nx, ny ] = pts[ i + 1 ];
    d += ` Q${ f( x ) },${ f( y ) } ${ f( ( x + nx ) / 2 ) },${ f( ( y + ny ) / 2 ) }`;
  }
  const last = pts[ pts.length - 1 ];
  return d + ` L${ f( last[ 0 ] ) },${ f( last[ 1 ] ) }`;
}

/** One stretch of the right-hand outline: half-width `h0` at `y0` growing to
 *  `h1` at `y1`. `e` is the exponent fitted to the icon — 0.85 for the shaft,
 *  1.0 for the mid section, 0.72 for the legs. */
function taper( y0: number, y1: number, h0: number, h1: number, e: number, n: number ): Pt[] {
  const out: Pt[] = [];
  for ( let i = 0; i <= n; i++ ) {
    const t = i / n;
    out.push( [ CX + h0 + ( h1 - h0 ) * Math.pow( t, e ), y0 + ( y1 - y0 ) * t ] );
  }
  return out;
}

/** Mirror a right-hand stretch onto the left, reversed so it reads bottom-up. */
function mirror( pts: Pt[] ): Pt[] {
  return pts.map( ( [ x, y ] ) => [ 2 * CX - x, y ] as Pt ).reverse();
}

/** A clockwise rectangle — clockwise so every band winds with the two big
 *  contours and nonzero fill unions them. */
function band( x0: number, y0: number, x1: number, y1: number ): string {
  return `M${ f( x0 ) },${ f( y0 ) } L${ f( x1 ) },${ f( y0 ) }`
    + ` L${ f( x1 ) },${ f( y1 ) } L${ f( x0 ) },${ f( y1 ) } Z`;
}

/* -------------------------------------------------------------- the tower -- */

const SHAFT = taper( BLOCK_BOT, P2_TOP, SHAFT_TOP, P2_ABOVE, 0.85, 5 );
const MIDDLE = taper( P2_BOT, P1_TOP, P2_BELOW, P1_ABOVE, 1.0, 4 );
const LEGS = taper( P1_BOT, GROUND, LEG_TOP, FOOT_HALF, 0.72, 5 );

/**
 * The ground line and the arch cut into it: right foot inward to the arch's
 * springing, up and over as a quarter-ellipse each side, then out to the left
 * foot. Part of the outline, never a separate subpath — that is what makes the
 * arch a hole rather than a shape painted in the background colour.
 */
const ARCH = ` L${ f( CX + ARCH_HALF ) },${ f( GROUND ) }`
  + ` C${ f( CX + ARCH_HALF ) },${ f( GROUND - KAPPA * ( GROUND - ARCH_CROWN ) ) }`
  + ` ${ f( CX + KAPPA * ARCH_HALF ) },${ f( ARCH_CROWN ) } ${ f( CX ) },${ f( ARCH_CROWN ) }`
  + ` C${ f( CX - KAPPA * ARCH_HALF ) },${ f( ARCH_CROWN ) }`
  + ` ${ f( CX - ARCH_HALF ) },${ f( GROUND - KAPPA * ( GROUND - ARCH_CROWN ) ) }`
  + ` ${ f( CX - ARCH_HALF ) },${ f( GROUND ) }`
  + ` L${ f( CX - FOOT_HALF ) },${ f( GROUND ) }`;

/**
 * The whole tower as ONE closed contour, arch included. This is the OFF state
 * and the base layer of the ON state. Mast, pavilion, shaft, second platform,
 * mid section, first platform, legs, arch, and back up the other side.
 */
export const EIFFEL_SOLID = `M${ f( CX ) },${ f( TIP ) } L${ f( CX + MAST_HALF ) },${ f( BLOCK_TOP ) }`
  + ` L${ f( CX + BLOCK_HALF ) },${ f( BLOCK_TOP ) } L${ f( CX + BLOCK_HALF ) },${ f( BLOCK_BOT ) }`
  + ` L${ f( CX + SHAFT_TOP ) },${ f( BLOCK_BOT ) }`
  + chain( SHAFT )
  + ` L${ f( CX + P2_HALF ) },${ f( P2_TOP ) } L${ f( CX + P2_HALF ) },${ f( P2_BOT ) }`
  + ` L${ f( CX + P2_BELOW ) },${ f( P2_BOT ) }`
  + chain( MIDDLE )
  + ` L${ f( CX + P1_HALF ) },${ f( P1_TOP ) } L${ f( CX + P1_HALF ) },${ f( P1_BOT ) }`
  + ` L${ f( CX + LEG_TOP ) },${ f( P1_BOT ) }`
  + chain( LEGS )
  + ARCH
  + chain( mirror( LEGS ) )
  + ` L${ f( CX - P1_HALF ) },${ f( P1_BOT ) } L${ f( CX - P1_HALF ) },${ f( P1_TOP ) }`
  + ` L${ f( CX - P1_ABOVE ) },${ f( P1_TOP ) }`
  + chain( mirror( MIDDLE ) )
  + ` L${ f( CX - P2_HALF ) },${ f( P2_BOT ) } L${ f( CX - P2_HALF ) },${ f( P2_TOP ) }`
  + ` L${ f( CX - P2_ABOVE ) },${ f( P2_TOP ) }`
  + chain( mirror( SHAFT ) )
  + ` L${ f( CX - BLOCK_HALF ) },${ f( BLOCK_BOT ) } L${ f( CX - BLOCK_HALF ) },${ f( BLOCK_TOP ) }`
  + ` L${ f( CX - MAST_HALF ) },${ f( BLOCK_TOP ) } Z`;

/**
 * Everything below the first platform — the two legs and the arch between them —
 * as its own closed contour, carrying the same arch hole. Painted over the body
 * in the deeper amber: the floodlights stand on the ground and the feet are
 * where the tower burns hottest.
 */
export const EIFFEL_BASE = `M${ f( CX + P1_ABOVE ) },${ f( P1_TOP ) }`
  + ` L${ f( CX + P1_HALF ) },${ f( P1_TOP ) } L${ f( CX + P1_HALF ) },${ f( P1_BOT ) }`
  + ` L${ f( CX + LEG_TOP ) },${ f( P1_BOT ) }`
  + chain( LEGS )
  + ARCH
  + chain( mirror( LEGS ) )
  + ` L${ f( CX - P1_HALF ) },${ f( P1_BOT ) } L${ f( CX - P1_HALF ) },${ f( P1_TOP ) } Z`;

/** The three platform bands — first, second, top pavilion — bottom to top. */
export const EIFFEL_DECKS = [
  band( CX - P1_HALF, P1_TOP, CX + P1_HALF, P1_BOT ),
  band( CX - P2_HALF, P2_TOP, CX + P2_HALF, P2_BOT ),
  band( CX - BLOCK_HALF, BLOCK_TOP, CX + BLOCK_HALF, BLOCK_BOT ),
].join( ' ' );

/** The lamp rows running along the top of each platform, held back from the
 *  ends so the ledge keeps its full span on a pale surface. */
export const EIFFEL_LAMPS = [
  band( CX - P1_HALF + LAMP_INSET, P1_TOP, CX + P1_HALF - LAMP_INSET, P1_TOP + LAMP_H ),
  band( CX - P2_HALF + LAMP_INSET, P2_TOP, CX + P2_HALF - LAMP_INSET, P2_TOP + LAMP_H ),
  band( CX - BLOCK_HALF + 1.4, BLOCK_TOP, CX + BLOCK_HALF - 1.4, BLOCK_TOP + 2.2 ),
].join( ' ' );

/** The shadow under each ledge. Dark on gold reads the same on the near-black
 *  strip and on the near-white glass, which is why the ledges are defined by a
 *  dark line and not by a bright one. */
export const EIFFEL_LEDGE_SHADOW = [
  band( CX - P1_HALF, P1_BOT - SHADE_H, CX + P1_HALF, P1_BOT ),
  band( CX - P2_HALF, P2_BOT - SHADE_H, CX + P2_HALF, P2_BOT ),
  band( CX - BLOCK_HALF, BLOCK_BOT - 1.6, CX + BLOCK_HALF, BLOCK_BOT ),
].join( ' ' );

/* ----------------------------------------------------------------- colours --
 * Paris's palette names two of these for this exact object — Eiffel Gold and
 * Deep Eiffel are the tower lit at night — so the ON state is gold and the only
 * question was how to keep gold legible. Measured on both surfaces it has to
 * land on, the near-black classic strip #422C21 and the near-white iOS-26 glass
 * #F7FEFF, every gold in the palette clears 5.5:1 on the strip and only ~2.3:1
 * on the glass. That is why the ledges carry a Bordeaux shadow line: dark on
 * gold is the same relationship on either surface, so the structure holds even
 * where the body itself is quiet.
 */

/** Eiffel Gold — the body. 5.64:1 on the strip, 2.26:1 on the glass. */
export const EIFFEL_GOLD = '#D5A24C';
/** Deep Eiffel — the floodlit feet and the platform bands. Barely darker than
 *  the body but a good deal more saturated, so it separates by chroma rather
 *  than by tone and never chops the tower into stripes the way a red-and-white
 *  band would — that is Tokyo's livery, not this one. */
export const EIFFEL_DEEP = '#DE9D2E';
/** Champagne Gold — the lamp rows. 1.54:1 on the glass, which is fine for a
 *  3-unit line sitting on a gold band and is exactly why it is inset. */
export const EIFFEL_LAMP = '#F1C970';
/** Bordeaux — the shadow under each ledge. 2.09:1 on the strip and 6.09:1 on
 *  the glass; against the gold it sits on it is ~2.6:1 either way. */
export const EIFFEL_SHADE = '#994753';

/**
 * The idle tone: the whole tower flat in one dusty mauve, arch still cut.
 *
 * Sits between Mauve Salon and Rose Smoke, and it is there because both of those
 * fail one surface — Rose Smoke reads 5.58:1 on the strip but 2.29:1 on the
 * glass, Mauve Salon 1.41:1 and 9.05:1, each fine where it was picked and gone
 * where it was not. This one measures 3.56:1 on the strip and 3.58:1 on the
 * glass: the same idle weight on both, which is the trap Roraima's Dry Grass and
 * Istanbul's first teals both fell into. Desaturated enough to read as unlit
 * next to the gold.
 */
export const EIFFEL_OFF = '#A9788A';
